// A small IndexedDB store for collections the app re-reads on every boot.
//
// The reason this exists: every cache in this app is in-memory — the Redux
// store, the `fetchOnce` Maps, the module registry — so navigating between
// pages is free while *reloading* re-downloads everything. `users` (~4k docs)
// and `lots` (~5-10k docs) are several megabytes of that, and until they land
// the page's own query is queued behind them on Firestore's single connection.
//
// Reading them from IndexedDB paints the page with real data in a few hundred
// milliseconds instead of a few seconds; the network refresh still runs behind
// it, so the data is at most seconds stale.
//
// localStorage is not an option here — it caps at ~5MB and is synchronous, so
// it would block the main thread with exactly the payload we are trying to
// keep off it. IndexedDB is async and has no practical size limit.
//
// Every failure path resolves rather than rejects: a private window, a blocked
// upgrade or a full disk should cost the head start, never the app.

const DB_NAME = "algCache";
const DB_VERSION = 1;
const STORE = "collections";

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch (error) {
      resolve(null);
    }
  });
  return dbPromise;
};

const withStore = async (mode, run) => {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const store = tx.objectStore(STORE);
      let result = null;
      run(store, (value) => {
        result = value;
      });
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch (error) {
      resolve(null);
    }
  });
};

/**
 * @returns {Promise<{value: any, savedAt: number}|null>} null when absent,
 *   unreadable, or older than `maxAgeMs`.
 */
export const readCache = async (key, { maxAgeMs } = {}) => {
  const record = await withStore("readonly", (store, set) => {
    const request = store.get(key);
    request.onsuccess = () => set(request.result || null);
  });
  if (!record || !record.savedAt) return null;
  if (maxAgeMs && Date.now() - record.savedAt > maxAgeMs) return null;
  return record;
};

// Deferred to idle time: structured-cloning a few megabytes is real work, and
// nothing is waiting on it — the store already has the data.
export const writeCache = (key, value) => {
  const save = () =>
    withStore("readwrite", (store) => {
      store.put({ value, savedAt: Date.now() }, key);
    });

  if (typeof window !== "undefined" && window.requestIdleCallback) {
    window.requestIdleCallback(save, { timeout: 4000 });
  } else {
    setTimeout(save, 0);
  }
};

export const clearCache = (key) =>
  withStore("readwrite", (store) => {
    store.delete(key);
  });
